import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../api/api';
import { EventOrder, AdditionalService, EventVenue } from '../../types';
import { extractApiErrorMessage } from '../../utils/apiError';

interface EventOrderState {
  orders: EventOrder[];
  additionalServices: AdditionalService[];
  eventVenues: EventVenue[];
  loading: boolean;
  currentRequestId?: string;
}

const initialState: EventOrderState = { orders: [], additionalServices: [], eventVenues: [], loading: false };

export const fetchEventOrders = createAsyncThunk('eventOrder/fetchAll', async (options?: { excludeCompleted?: boolean; statusId?: number }) => {
  try {
    const res = await api.get<EventOrder[]>('/eventorders', {
      params: {
        ...(options?.excludeCompleted ? { excludeCompleted: true } : {}),
        ...(options?.statusId ? { statusId: options.statusId } : {}),
      },
    });
    return res.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown } };
    console.error('fetchEventOrders thunk failed', error);
    if (axiosError.response && axiosError.response.data !== undefined) {
      console.error('fetchEventOrders response body', axiosError.response.data);
    }
    throw error;
  }
});

export const createEventOrder = createAsyncThunk('eventOrder/create', async (data: any, { rejectWithValue }) => {
  try {
    const res = await api.post<EventOrder>('/eventorders', data);
    return res.data;
  } catch (error: unknown) {
    return rejectWithValue(extractApiErrorMessage(error, 'Не вдалося створити замовлення.'));
  }
});

export const updateEventOrder = createAsyncThunk(
  'eventOrder/update',
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    try {
      const res = await api.put<EventOrder>(`/eventorders/${id}`, data);
      return res.data;
    } catch (error: unknown) {
      return rejectWithValue(extractApiErrorMessage(error, 'Не вдалося зберегти замовлення.'));
    }
  }
);

export const deleteEventOrder = createAsyncThunk('eventOrder/delete', async (id: number) => {
  await api.delete(`/eventorders/${id}`);
  return id;
});

export const fetchAdditionalServices = createAsyncThunk('eventOrder/fetchServices', async () => {
  const res = await api.get<AdditionalService[]>('/additionalservices');
  return res.data;
});

export const createAdditionalService = createAsyncThunk('eventOrder/createService', async (data: any) => {
  const res = await api.post<AdditionalService>('/additionalservices', data);
  return res.data;
});

export const updateAdditionalService = createAsyncThunk(
  'eventOrder/updateService',
  async ({ id, data }: { id: number; data: any }) => {
    const res = await api.put<AdditionalService>(`/additionalservices/${id}`, data);
    return res.data;
  }
);

export const deleteAdditionalService = createAsyncThunk('eventOrder/deleteService', async (id: number) => {
  await api.delete(`/additionalservices/${id}`);
  return id;
});

export const fetchEventVenues = createAsyncThunk('eventOrder/fetchVenues', async () => {
  const res = await api.get<EventVenue[]>('/eventvenues');
  return res.data;
});

export const createEventVenue = createAsyncThunk('eventOrder/createVenue', async (data: any) => {
  const res = await api.post<EventVenue>('/eventvenues', data);
  return res.data;
});

export const updateEventVenue = createAsyncThunk(
  'eventOrder/updateVenue',
  async ({ id, data }: { id: number; data: any }) => {
    const res = await api.put<EventVenue>(`/eventvenues/${id}`, data);
    return res.data;
  }
);

export const deleteEventVenue = createAsyncThunk('eventOrder/deleteVenue', async (id: number) => {
  await api.delete(`/eventvenues/${id}`);
  return id;
});

const eventOrderSlice = createSlice({
  name: 'eventOrder',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventOrders.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchEventOrders.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.orders = action.payload;
        state.loading = false;
        state.currentRequestId = undefined;
      })
      .addCase(fetchEventOrders.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.currentRequestId = undefined;
      })
      .addCase(createEventOrder.fulfilled, (state, action) => {
        state.orders.push(action.payload);
      })
      .addCase(updateEventOrder.fulfilled, (state, action) => {
        const idx = state.orders.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.orders[idx] = action.payload;
      })
      .addCase(deleteEventOrder.fulfilled, (state, action) => {
        state.orders = state.orders.filter((i) => i.id !== action.payload);
      })
      .addCase(fetchAdditionalServices.fulfilled, (state, action) => {
        state.additionalServices = action.payload;
      })
      .addCase(createAdditionalService.fulfilled, (state, action) => {
        state.additionalServices.push(action.payload);
      })
      .addCase(updateAdditionalService.fulfilled, (state, action) => {
        const idx = state.additionalServices.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.additionalServices[idx] = action.payload;
      })
      .addCase(deleteAdditionalService.fulfilled, (state, action) => {
        state.additionalServices = state.additionalServices.filter((i) => i.id !== action.payload);
      })
      .addCase(fetchEventVenues.fulfilled, (state, action) => {
        state.eventVenues = action.payload;
      })
      .addCase(createEventVenue.fulfilled, (state, action) => {
        state.eventVenues.push(action.payload);
      })
      .addCase(updateEventVenue.fulfilled, (state, action) => {
        const idx = state.eventVenues.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.eventVenues[idx] = action.payload;
      })
      .addCase(deleteEventVenue.fulfilled, (state, action) => {
        state.eventVenues = state.eventVenues.filter((i) => i.id !== action.payload);
      });
  },
});

export default eventOrderSlice.reducer;
